import { DevToolsButton } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsButton';
import { DevToolsCopyBox } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsCopyBox';
import { DevToolsDirectionBadge } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsDirectionBadge';
import { DevToolsMicroLabel } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsMicroLabel';
import type { DebugRecordHeaderProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugRecord/DebugRecordHeader/index.types';
import { useDebugContact } from 'Modules/softphone/components/SoftphoneDevTools/hooks';
import {
  DIRECTION,
  SURFACE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import { formatTimestamp } from 'Modules/softphone/components/SoftphoneDevTools/utils';
import React from 'react';
import { cn } from '~/lib/utils';

/**
 * DebugRecordHeader - Which leg you are looking at
 *
 * Stays identical across all four sections, so switching section never costs
 * the reader the answer to "whose call is this".
 */
export const DebugRecordHeader: React.FC<DebugRecordHeaderProps> = ({
  info,
  onDownload,
}) => {
  const { displayName, phoneNumber } = useDebugContact(info.phoneNumber);

  const times = [
    { label: 'Start', value: info.startTime },
    { label: 'Answer', value: info.answerTime },
    { label: 'End', value: info.endTime },
  ];

  const stripe =
    info.direction === 'inbound'
      ? DIRECTION.inbound.stripe
      : DIRECTION.outbound.stripe;

  return (
    // The stripe repeats the direction colour from the row you came from, so
    // the record is visibly the thing you just clicked.
    <div
      className={cn(SURFACE.card, 'border-l-[3px] p-3', stripe)}
    >
      <div className="flex items-start gap-[9px]">
        <DevToolsDirectionBadge direction={info.direction} compact />

        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold text-zinc-200">
            {displayName}
          </div>
          <div className="truncate font-sp-mono text-[11px] text-zinc-400">
            {phoneNumber}
          </div>
        </div>

        <DevToolsButton variant="export" onClick={onDownload}>
          ↓ .txt
        </DevToolsButton>
      </div>

      <DevToolsCopyBox value={info.callId} label="Call ID" className="mt-2.5" />

      <div className="mt-2.5 grid grid-cols-3 gap-2">
        {times.map(({ label, value }) => (
          <div key={label}>
            <DevToolsMicroLabel>{label}</DevToolsMicroLabel>
            <div className="mt-px font-sp-mono text-xs text-zinc-200">
              {formatTimestamp(value, { fallback: '—' })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
